import React, { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';

import { withSiteServedAdWrapper } from 'site-modules/shared/components/native-ad/site-served-ad-wrapper/site-served-ad-wrapper';
import {
  AdComponentDefaultProps,
  AdComponentPropTypes,
  SiteServedAdDefaultProps,
  SiteServedAdPropTypes,
} from 'site-modules/shared/components/ad-unit/ad-unit-prop-types';
import {
  SegmentListingCreativeConfigDefaultProps,
  SegmentListingCreativeConfigPropTypes,
} from 'client/data/models/native-ads-creative-config';
import { addZipCode, definePlatform } from 'site-modules/shared/components/native-ad/utils/utils';
import classnames from 'classnames';
import Card from 'reactstrap/lib/Card';
import { DfpTracking } from 'site-modules/shared/components/native-ad/utils/dfp-tracking';
import { AdLabel } from 'site-modules/shared/components/native-ad/native-ad-components/ad-label/ad-label';
import { CtaButtonText } from 'site-modules/shared/components/native-ad/native-ad-components/cta-button-text/cta-button-text';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { siteServedAdTrackingWrapper } from 'site-modules/shared/components/native-ad/site-served-ad-tracking-wrapper/site-served-ad-tracking-wrapper';
import { SEGMENT_LISTING_AD } from 'site-modules/shared/components/native-ad/utils/constants';
import { Disclaimer } from 'site-modules/shared/components/native-ad/native-ad-components/disclaimer/disclaimer';

import './filmstrip-type-ranking-featured.scss';

function FilmstripTypeRankingFeaturedUI({
  adRef,
  adClickTrackingListener,
  adLoadTrackingListener,
  adTrackers,
  mobile = SiteServedAdDefaultProps.mobile,
  creativeConfig = SegmentListingCreativeConfigDefaultProps,
  position = SiteServedAdDefaultProps.position,
  currentVehicle = SiteServedAdDefaultProps.currentVehicle,
  wrapperClass = null,
  ivtClassName = SiteServedAdDefaultProps.ivtClassName,
  inlineStyle = null,
  cardClass,
  isAds12484Enabled = false,
  ads12491Recipe,
}) {
  const {
    siteServedAdsUpdater,
    zipCode,
    creativeConfigData: {
      logo,
      photo,
      headline,
      accolades,
      cta,
      linkDisclaimer,
      disclaimerCopy,
      lineItemId,
      siteServedCreativeId,
      linkDisplayUrl,
      target,
      msrp,
    },
    traffickingData: { clickTracker },
  } = creativeConfig;

  const loadTrackingListener = useCallback(
    event => {
      adLoadTrackingListener(event, cta);
    },
    [adLoadTrackingListener, cta]
  );

  const ctaURL = addZipCode(clickTracker[definePlatform(mobile)], zipCode);
  const ctaTarget = target || '_blank';

  const nativeStyle = SEGMENT_LISTING_AD.NATIVE_STYLE;
  const renderChalHeader = (
    <Fragment>
      <ContentFragment componentToUse="h3" classes={'mmy-header pe-1 mb-0 text-blue-50'}>
        {headline}
      </ContentFragment>
      <div className="text-cool-gray-30 mx-auto mt-0_5">{!!accolades?.length && accolades.join(' ')}</div>
    </Fragment>
  );
  const renderAds12484CardContent = (
    <div className="d-flex flex-column pt-2 pb-1 pb-lg-0_25 px-1">
      <AdLabel
        styles={{
          top: '12px',
          right: '12px',
        }}
      />
      <div className="pos-a pe-none rank heading-6 bg-blue-40 text-cool-gray-100 p-0_5 text-shadow-contrast">
        Also Consider
      </div>
      <div className="pt-1 mb-0 d-xl-flex">
        <figure className="w-50 mb-0 me-1 me-lg-0_5 me-xl-1 float-start overflow-hidden rounded-8">
          <img
            className="img-fluid w-100 h-100"
            src={photo}
            alt={headline}
            style={{ aspectRatio: '3 / 2', objectFit: 'cover' }}
          />
        </figure>
        <div className="hidden-xl-down w-50">{renderChalHeader}</div>
        <div className="hidden-xl-up">{renderChalHeader}</div>
      </div>
      <hr className="my-1 w-100" />
      <div className="d-flex align-items-baseline mb-0_25 justify-content-between text-cool-gray-30">
        <span>Starting MSRP</span>
        <span className="fw-bold">{msrp}</span>
      </div>
      <div className="d-flex flex-column align-items-center pt-1">
        <div className="btn size-16 btn-sm btn-bg-white btn-outline-blue-50 px-lg-3_5 cta w-100">
          <CtaButtonText text={cta || 'Learn More'} />
        </div>
        <div className="my-0_25 text-blue-50 small fw-medium cta-small text-center">{linkDisplayUrl}</div>
      </div>
    </div>
  );

  const renderAds12491Chal2CardContent = (
    <div className="d-flex flex-column pb-1 pb-lg-0_25">
      <AdLabel
        styles={{
          top: '12px',
          right: '12px',
        }}
      />
      <div className="pos-a pe-none rank heading-5 bg-blue-50 text-cool-gray-100 p-1 text-shadow-contrast">
        Also Consider
      </div>
      <figure className="mb-0 overflow-hidden">
        <img
          className="img-fluid w-100 h-100"
          src={photo}
          alt={headline}
          style={{ aspectRatio: '3 / 2', objectFit: 'cover' }}
        />
      </figure>
      <div
        className="card-details-container py-1 px-1 d-flex flex-column"
        style={{ marginTop: '-72px', minHeight: '106px' }}
      >
        <div className="d-flex bg-blue-100 px-1_25 py-1 large-card-heading justify-content-between align-items-center mb-1">
          <ContentFragment componentToUse="h2" classes={'mmy-header pe-1 mb-0 text-blue-30 hidden-lg-down size-20'}>
            {headline}
          </ContentFragment>
          <ContentFragment componentToUse="h2" classes={'mmy-header pe-1 mb-0 text-blue-30 hidden-lg-up'}>
            {headline}
          </ContentFragment>
          <div className="d-flex align-items-baseline mb-0_25 text-cool-gray-30 ps-1_5 pe-0_5d-flex flex-column justify-content-center msrp">
            <span className="fw-bold size-24 d-block">{msrp}</span>
            <span className="size-14">Starting MSRP</span>
          </div>
        </div>
        <div className="text-cool-gray-30 mt-0_5 fw-medium">{!!accolades?.length && accolades.join(' ')}</div>
        <hr className="my-1 w-100" />
        <div className="d-flex flex-column align-items-center pt-1">
          <div className="btn size-16 btn-sm btn-bg-white btn-outline-blue-50 px-lg-3_5 w-100 cta">
            <CtaButtonText text={cta || 'Learn More'} />
          </div>
          <div className="my-0_25 text-blue-50 small fw-medium cta-small text-center">{linkDisplayUrl}</div>
        </div>
      </div>
    </div>
  );
  const isAds12491Chal2 = ads12491Recipe === 'chal2';

  const showContent = () => {
    if (isAds12484Enabled) {
      // Type page chal
      return renderAds12484CardContent;
    }
    if (isAds12491Chal2) {
      // Subtype page chal2
      return renderAds12491Chal2CardContent;
    }
    return (
      // default
      <div className="mx-0 d-flex flex-column flex-lg-row">
        <figure
          className="jellybean mx-0 my-0 d-flex align-items-center overflow-hidden"
          style={{
            aspectRatio: '16 / 9',
            flex: '1 0 45%',
            maxWidth: '527px',
            minWidth: 'min(100%, 440px)',
          }}
        >
          <img src={photo} alt="" loading="lazy" className="photo w-100" />
        </figure>
        <div
          className="content pos-r px-1 py-2 py-lg-1 d-flex flex-column justify-content-center text-center"
          style={{
            flex: '1 0 50%',
          }}
        >
          <div className="logo w-100 pos-a overflow-hidden" style={{ top: '12px', left: '12px', height: '20px' }}>
            <img src={logo} alt="" loading="lazy" className="logo left-0 pos-a center-y" style={{ height: '20px' }} />
          </div>
          <AdLabel
            styles={{
              top: '12px',
              right: '12px',
            }}
          />
          <div className="size-12 mb-0_5 text-blue-50 fw-bold">FEATURED NEW VEHICLE</div>
          <ContentFragment classes={'mb-1 headline text-cool-gray-30'}>{headline}</ContentFragment>
          <div className="text-cool-gray-30 mb-2 mx-auto" style={{ maxWidth: '408px' }}>
            {!!accolades?.length && accolades.join(' ')}
          </div>
          <div>
            <div className="btn size-16 btn-sm btn-bg-white btn-outline-blue-50 px-3_5">
              <CtaButtonText text={cta || 'Learn More'} />
            </div>
            <div className="my-0_25 text-blue-50 small fw-medium cta-small text-md-center">{linkDisplayUrl}</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={classnames(wrapperClass, ivtClassName, {
        'mx-auto my-1 pb-0 my-lg-3_5': isAds12491Chal2,
      })}
      style={isAds12491Chal2 ? { ...inlineStyle, maxWidth: '526px' } : inlineStyle}
    >
      <Card
        className={classnames(
          'filmstrip-type-ranking-featured border-0 rounded-16 overflow-hidden mx-auto pos-r',
          cardClass,
          {
            'is-ads12484-enabled': isAds12484Enabled,
            'is-ads12491-chal2': isAds12491Chal2,
          }
        )}
        data-tracking-parent={nativeStyle}
        data-viewability={`${nativeStyle}-${position}`}
        innerRef={adRef}
      >
        <DfpTracking
          lineItemId={lineItemId}
          siteServedCreativeId={siteServedCreativeId}
          mobile={mobile}
          nativeStyle={nativeStyle}
          position={position}
          slotRenderEndListener={loadTrackingListener}
          siteServedAdsUpdater={siteServedAdsUpdater}
          currentVehicle={currentVehicle}
        />
        {adTrackers}
        <a
          href={ctaURL}
          target={ctaTarget}
          rel="noopener noreferrer"
          className="site-served-ad-link no-focus text-decoration-none h-100"
          onClick={adClickTrackingListener}
        >
          {showContent()}
        </a>
        {disclaimerCopy && (
          <Disclaimer
            linkDisclaimer={linkDisclaimer}
            disclaimerText={disclaimerCopy}
            buttonClasses="pos-a bottom-0 right-0"
          />
        )}
      </Card>
    </div>
  );
}

FilmstripTypeRankingFeaturedUI.propTypes = {
  ...SiteServedAdPropTypes,
  creativeConfig: SegmentListingCreativeConfigPropTypes,
  wrapperClass: PropTypes.string,
  inlineStyle: PropTypes.shape({}),
  isAds12484Enabled: PropTypes.bool,
  ads12491Recipe: PropTypes.string,
};

export const FilmstripTypeRankingFeaturedSiteServedAd = siteServedAdTrackingWrapper(FilmstripTypeRankingFeaturedUI);
const FilmstripTypeRankingFeaturedWrapper = withSiteServedAdWrapper(FilmstripTypeRankingFeaturedSiteServedAd);

export function FilmstripTypeRankingFeatured({
  type = null,
  cardClass = null,
  mobile = AdComponentDefaultProps.mobile,
  position = AdComponentDefaultProps.position,
  slotRenderEndListener = AdComponentDefaultProps.slotRenderEndListener,
  placeholderComponent = AdComponentDefaultProps.placeholderComponent,
  placeholderComponentProps = AdComponentDefaultProps.placeholderComponentProps,
  customTargeting = AdComponentDefaultProps.customTargeting,
  vehicle = AdComponentDefaultProps.vehicle,
  dependedOnCurrentVehicle = AdComponentDefaultProps.dependedOnCurrentVehicle,
  wrapperClass = AdComponentDefaultProps.wrapperClass,
  siteServedAdsEnabled = AdComponentDefaultProps.siteServedAdsEnabled,
  breakpoints = AdComponentDefaultProps.breakpoints,
  isIvt = AdComponentDefaultProps.isIvt,
  ivtClassName = AdComponentDefaultProps.ivtClassName,
  ...restProps
}) {
  const currentVehicle = type ? { type: { niceName: type } } : null;

  return (
    <FilmstripTypeRankingFeaturedWrapper
      ivtClassName={ivtClassName}
      isIvt={isIvt}
      breakpoints={breakpoints}
      siteServedAdsEnabled={siteServedAdsEnabled}
      wrapperClass={wrapperClass}
      dependedOnCurrentVehicle={dependedOnCurrentVehicle}
      vehicle={vehicle}
      customTargeting={customTargeting}
      placeholderComponentProps={placeholderComponentProps}
      placeholderComponent={placeholderComponent}
      slotRenderEndListener={slotRenderEndListener}
      position={position}
      mobile={mobile}
      cardClass={cardClass}
      {...restProps}
      currentVehicle={currentVehicle}
      adType={SEGMENT_LISTING_AD}
    />
  );
}

FilmstripTypeRankingFeatured.propTypes = {
  ...AdComponentPropTypes,
  type: PropTypes.string,
  cardClass: PropTypes.string,
};
